import { useState } from 'react'
import {
  accountCodeError,
  categoryEnum,
  fundEnum,
  normalizeAccountCode,
  type BudgetCode,
  type Category,
  type Fund,
  type NamedCodeLike,
  type NewOwnedBudgetCode,
} from '@budgetmonkey/shared'
import { Button, ErrorNote, Field, Input, Select } from '@/components/ui'
import { AliasEditor } from './AliasEditor'

/**
 * Add or edit an owned budget code — workflow 7.9.
 *
 * Hand-rolled rather than react-hook-form: the only non-trivial validation is
 * the account code and the aliases, both of which live in shared/domain and are
 * already tested there. A form library would add indirection without removing
 * any of the logic that actually matters here.
 */

const CATEGORY_LABELS: Record<Category, string> = {
  travel: 'Travel',
  conference: 'Conference',
  tech: 'Tech',
  meals: 'Meals',
  services: 'Services',
  other: 'Other',
}

const FUND_LABELS: Record<Fund, string> = {
  main: 'Main fund',
  grant: 'Grant fund',
}

export interface BudgetCodeFormValues extends NewOwnedBudgetCode {}

export function BudgetCodeForm({
  initial,
  existingCodes,
  currentCodeId,
  submitLabel,
  onSubmit,
  onCancel,
  pending,
  error,
}: {
  initial?: Partial<BudgetCode> | undefined
  existingCodes: readonly NamedCodeLike[]
  currentCodeId?: string | undefined
  submitLabel: string
  onSubmit: (values: BudgetCodeFormValues) => void
  onCancel: () => void
  pending: boolean
  error?: string | undefined
}) {
  const [code, setCode] = useState(initial?.code ?? '')
  const [displayName, setDisplayName] = useState(initial?.displayName ?? '')
  const [shortLabel, setShortLabel] = useState(initial?.shortLabel ?? '')
  const [fund, setFund] = useState<Fund>(initial?.fund ?? 'main')
  const [category, setCategory] = useState<Category>(initial?.category ?? 'other')
  const [aliases, setAliases] = useState<string[]>([...(initial?.aliases ?? [])])
  const [beginningBalance, setBeginningBalance] = useState(
    initial?.beginningBalance != null ? String(initial.beginningBalance) : '',
  )
  const [threshold, setThreshold] = useState(
    String(initial?.alertThresholdPercent ?? 80),
  )
  const [touched, setTouched] = useState(false)

  const codeProblem = touched ? accountCodeError(code) : null
  const normalizedCode = normalizeAccountCode(code)
  const balanceNumber = Number(beginningBalance)
  const balanceProblem =
    touched && (beginningBalance.trim() === '' || Number.isNaN(balanceNumber) || balanceNumber < 0)
      ? 'Enter a dollar amount of zero or more.'
      : null

  const canSubmit =
    normalizedCode !== null &&
    displayName.trim() !== '' &&
    shortLabel.trim() !== '' &&
    beginningBalance.trim() !== '' &&
    !Number.isNaN(balanceNumber) &&
    balanceNumber >= 0

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    setTouched(true)
    if (!canSubmit || normalizedCode === null) return
    onSubmit({
      code: normalizedCode,
      displayName: displayName.trim(),
      shortLabel: shortLabel.trim(),
      fund,
      category,
      aliases,
      beginningBalance: balanceNumber,
      alertThresholdPercent: Number(threshold) || 80,
    })
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5" noValidate>
      {error !== undefined && <ErrorNote>{error}</ErrorNote>}

      <Field
        label="Account code"
        required
        error={codeProblem ?? undefined}
        hint="Nine segments. Spaces are accepted and converted to hyphens."
      >
        <Input
          value={code}
          onChange={(e) => setCode(e.target.value)}
          onBlur={() => setTouched(true)}
          placeholder="10-2818-580-000-00-000-000-329-0000"
          invalid={codeProblem !== null}
          className="font-mono text-sm"
          inputMode="numeric"
          autoComplete="off"
        />
      </Field>

      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Display name" required>
          <Input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Travel (Transportation, Lodging, Meals)"
          />
        </Field>

        <Field
          label="Short label"
          required
          hint="Shown where space is tight — dashboard cards, the code picker and lookup. The packet prints the full account code."
        >
          <Input
            value={shortLabel}
            onChange={(e) => setShortLabel(e.target.value)}
            placeholder="Travel"
            maxLength={24}
          />
        </Field>

        <Field label="Fund" required>
          <Select value={fund} onChange={(e) => setFund(e.target.value as Fund)}>
            {fundEnum.options.map((option) => (
              <option key={option} value={option}>
                {FUND_LABELS[option]}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Category" required hint="For your own reference — shown on the code's page.">
          <Select
            value={category}
            onChange={(e) => setCategory(e.target.value as Category)}
          >
            {categoryEnum.options.map((option) => (
              <option key={option} value={option}>
                {CATEGORY_LABELS[option]}
              </option>
            ))}
          </Select>
        </Field>

        <Field
          label="Beginning balance"
          required
          error={balanceProblem ?? undefined}
          hint="Dollars authorized at fiscal-year start."
        >
          <Input
            value={beginningBalance}
            onChange={(e) => setBeginningBalance(e.target.value)}
            onBlur={() => setTouched(true)}
            placeholder="0.00"
            inputMode="decimal"
            invalid={balanceProblem !== null}
          />
        </Field>

        <Field label="Alert threshold" hint="Warn once this percent is spent.">
          <Input
            value={threshold}
            onChange={(e) => setThreshold(e.target.value)}
            inputMode="numeric"
            placeholder="80"
          />
        </Field>
      </div>

      <AliasEditor
        code={normalizedCode ?? code}
        aliases={aliases}
        onChange={setAliases}
        existingCodes={existingCodes}
        currentCodeId={currentCodeId}
      />

      <div className="flex gap-3 pt-2">
        <Button type="submit" variant="primary" disabled={pending}>
          {pending ? 'Saving…' : submitLabel}
        </Button>
        <Button type="button" onClick={onCancel} disabled={pending}>
          Cancel
        </Button>
      </div>
    </form>
  )
}
